import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { attendanceApi } from '../axios';
import { generateAttendanceForEmployees } from '@/lib/attendance-mock-generator';

// ─── OT merge helpers ────────────────────────────────────────────────────────

function parseOTMinutes(ot) {
  if (!ot || ot === '0h 0m') return 0;
  const m = ot.match(/(\d+)h\s*(\d+)m/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
}

function formatOTFromMin(minutes) {
  const t = Math.max(0, minutes);
  return `${Math.floor(t / 60)}h ${t % 60}m`;
}

export const useAttendanceSummary = (date, companyId) => useQuery({
  queryKey: ['attendance', 'summary', date, companyId],
  queryFn: () => attendanceApi.get('/v1/attendance/summary', { params: { date } }).then(r => r.data),
  refetchInterval: 5 * 60 * 1000,
  staleTime: 0,
});

export const useDailyAttendance = (params, companyId) => useQuery({
  queryKey: ['attendance', 'daily', params, companyId],
  queryFn: () => attendanceApi.get('/v1/attendance/daily', { params }).then(r => r.data),
  staleTime: 0,
  refetchOnWindowFocus: true,
});

export const useMonthlyAttendance = (params) => useQuery({
  queryKey: ['attendance', 'monthly', params],
  queryFn: () => attendanceApi.get('/v1/attendance/monthly', { params }).then(r => r.data),
  enabled: !!params?.employee_id,
});

export const useEmployeeAttendance = (empId) => useQuery({
  queryKey: ['attendance', 'employee', empId],
  queryFn: () => attendanceApi.get('/v1/attendance/monthly', { params: { employee_id: empId } }).then(r => r.data),
  enabled: !!empId,
  staleTime: 30000,
});

export const useCheckIn = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => attendanceApi.post('/v1/attendance/check-in', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance'] }),
  });
};

export const useCheckOut = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => attendanceApi.post('/v1/attendance/check-out', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance'] }),
  });
};

export const useRegularizeAttendance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => attendanceApi.post('/v1/attendance/regularize', payload).then(r => r.data),
    onSuccess: () => {
      // invalidate marks stale; refetchQueries forces an immediate network fetch
      qc.invalidateQueries({ queryKey: ['attendance'] });
      qc.refetchQueries({ queryKey: ['attendance', 'daily'] });
      qc.refetchQueries({ queryKey: ['attendance', 'summary'] });
    },
  });
};

// Dev/admin: clear all attendance records for a specific date
export const useClearAttendanceDate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date) =>
      attendanceApi.delete('/v1/attendance/daily', { params: { date } }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attendance'] });
      qc.invalidateQueries({ queryKey: ['punch-logs'] });
    },
  });
};

// Dev/admin: clear all punch-log records for a specific date
export const useClearPunchLogsDate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date) =>
      attendanceApi.delete('/v1/attendance/punch-logs', { params: { date } }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['punch-logs'] }),
  });
};

// ─── Monthly All-Employee Attendance ──────────────────────────────────────────

/**
 * Monthly attendance grid for ALL employees — used by Monthly Report, OT Report.
 * Data is generated from the REAL employee list (passed as `employees`) so reports
 * always show the logged-in company's employees, not hardcoded demo employees.
 *
 * @param {string} month      - 'YYYY-MM' e.g. '2026-06'
 * @param {object} filters    - optional { department, employee_id }
 * @param {string} companyId  - scoped by JWT on the service (Phase 1C)
 * @param {Array}  employees  - from useEmployees() — drives mock/initialData
 */
export const useMonthlyAllAttendance = (month, filters = {}, companyId, employees = []) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const generated = useMemo(
    () => generateAttendanceForEmployees(employees, month, filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees, month, JSON.stringify(filters)]
  );

  return useQuery({
    queryKey: ['attendance', 'monthly-all', month, filters, companyId, employees.map(e => e.id || e.employee_id).join(',')],
    queryFn: () =>
      attendanceApi
        .get('/v1/attendance/monthly-all', { params: { month, ...filters } })
        .then(r => {
          const realData = r.data?.data || [];

          // No real data yet → pure historical generator output
          if (!realData.length) return generated;

          // Merge: generated has pre-cutoff days, realData has post-cutoff days.
          // Spread by employee_id so each employee's days dict is combined.
          const genMap  = new Map((generated.data || []).map(e => [e.employee_id, e]));
          const realMap = new Map(realData.map(e => [e.employee_id, e]));
          const allIds  = new Set([...genMap.keys(), ...realMap.keys()]);

          const merged = Array.from(allIds).map(id => {
            const g  = genMap.get(id);
            const r2 = realMap.get(id);
            if (!r2) return g;
            if (!g)  return r2;
            return {
              ...g,
              days:  { ...g.days,  ...r2.days  },
              times: { ...g.times, ...r2.times },
              summary: {
                ...g.summary,
                P:  (g.summary?.P || 0) + (r2.summary?.P || 0),
                L:  (g.summary?.L || 0) + (r2.summary?.L || 0),
                A:  (g.summary?.A || 0) + (r2.summary?.A || 0),
                OT: formatOTFromMin(parseOTMinutes(g.summary?.OT) + parseOTMinutes(r2.summary?.OT)),
              },
            };
          });
          return { success: true, data: merged };
        })
        .catch(() => generated),
    initialData: employees.length > 0 ? generated : undefined,
    initialDataUpdatedAt: 0,
    staleTime: 30_000,
    retry: 0,
    enabled: !!month,
  });
};

// ─── Shifts ───────────────────────────────────────────────────────────────────

export const useShifts = () => useQuery({
  queryKey: ['shifts'],
  queryFn: () => attendanceApi.get('/v1/attendance/shifts').then(r => r.data),
  staleTime: 60_000,
});

export const useCreateShift = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => attendanceApi.post('/v1/attendance/shifts', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shifts'] }),
  });
};

export const useUpdateShift = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => attendanceApi.put(`/v1/attendance/shifts/${id}`, payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shifts'] }),
  });
};

export const useDeleteShift = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => attendanceApi.delete(`/v1/attendance/shifts/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shifts'] }),
  });
};

// ─── Shift Assignments ────────────────────────────────────────────────────────

export const useShiftAssignments = (params = {}) => useQuery({
  queryKey: ['shift-assignments', params],
  queryFn: () => attendanceApi.get('/v1/attendance/shift-assignments', { params }).then(r => r.data),
  staleTime: 30_000,
});

export const useAssignShift = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => attendanceApi.post('/v1/attendance/shift-assignments', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift-assignments'] }),
  });
};

export const useBulkAssignShift = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => attendanceApi.post('/v1/attendance/shift-assignments/bulk', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift-assignments'] }),
  });
};

export const useRemoveShiftAssignment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (employeeId) => attendanceApi.delete(`/v1/attendance/shift-assignments/${employeeId}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shift-assignments'] }),
  });
};

// ─── Cyclic Shifts ────────────────────────────────────────────────────────────

export const useCyclicPatterns = () => useQuery({
  queryKey: ['cyclic-patterns'],
  queryFn: () => attendanceApi.get('/v1/attendance/cyclic-patterns').then(r => r.data),
  staleTime: 60_000,
});

export const useCreateCyclicPattern = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => attendanceApi.post('/v1/attendance/cyclic-patterns', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cyclic-patterns'] }),
  });
};

export const useUpdateCyclicPattern = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => attendanceApi.put(`/v1/attendance/cyclic-patterns/${id}`, payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cyclic-patterns'] }),
  });
};

export const useDeleteCyclicPattern = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => attendanceApi.delete(`/v1/attendance/cyclic-patterns/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cyclic-patterns'] }),
  });
};

export const useCyclicAssignments = () => useQuery({
  queryKey: ['cyclic-assignments'],
  queryFn: () => attendanceApi.get('/v1/attendance/cyclic-assignments').then(r => r.data),
  staleTime: 30_000,
});

export const useAssignCyclicPattern = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => attendanceApi.post('/v1/attendance/cyclic-assignments', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cyclic-assignments'] });
      qc.invalidateQueries({ queryKey: ['shift-assignments'] });
    },
  });
};

export const useRemoveCyclicAssignment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (employeeId) => attendanceApi.delete(`/v1/attendance/cyclic-assignments/${employeeId}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cyclic-assignments'] }),
  });
};

// ─── Comp Off ─────────────────────────────────────────────────────────────────

export const useCompOffs = (params = {}) => useQuery({
  queryKey: ['comp-offs', params],
  queryFn: () => attendanceApi.get('/v1/attendance/comp-offs', { params }).then(r => r.data),
  staleTime: 30_000,
});

export const useCreateCompOff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => attendanceApi.post('/v1/attendance/comp-offs', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-offs'] }),
  });
};

export const useApproveCompOff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => attendanceApi.post(`/v1/attendance/comp-offs/${id}/approve`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-offs'] }),
  });
};

export const useRejectCompOff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) => attendanceApi.post(`/v1/attendance/comp-offs/${id}/reject`, { reason }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-offs'] }),
  });
};

export const useDeleteCompOff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => attendanceApi.delete(`/v1/attendance/comp-offs/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comp-offs'] }),
  });
};
